import { Link, Navigate } from "react-router-dom";
import { GUIDES, guidePath, clustersWithGuides, AUTHOR } from "@/content/guides";
import { Block, OnThisPage, slugify } from "@/pages/DocsPage";

// Renders one guide, or the guides index when no slug is given.
//
// The block renderers come from DocsPage because guides use the same block
// vocabulary — see the note there. What differs is the shell: a doc is
// navigated from a sidebar by someone who already bought, a guide is landed on
// from a search result by someone who has not, so this page leads with the
// answer and ends by pointing at the product pages.

// ── Index ────────────────────────────────────────────────────────────────────

function GuidesHome() {
  const clusters = clustersWithGuides();

  return (
    <div data-testid="guides-home">
      <section className="relative pt-32 md:pt-36 pb-10 border-b border-white/10 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }} />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-4">Guides</p>
          <h1 className="font-heading font-medium tracking-tight leading-[1.02] text-4xl md:text-5xl text-white max-w-3xl">
            How business calling actually works
          </h1>
          <p className="mt-5 text-lg text-neutral-400 max-w-2xl leading-relaxed">
            Plain explanations of cloud phone systems, AI voice agents, and the
            rules that apply to business calls in India — written for people
            deciding what to buy, not for people who already have.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-12 grid gap-10 md:grid-cols-2">
          {clusters.map((c) => (
            <div key={c.id} className="border border-white/10 bg-white/[0.02] p-7">
              <h2 className="font-heading font-medium text-xl text-white">{c.title}</h2>
              <p className="mt-2.5 text-sm text-neutral-500 leading-relaxed">{c.blurb}</p>

              {c.slugs.length ? (
                <ul className="mt-6 space-y-4">
                  {c.slugs.map((s) => (
                    <li key={s}>
                      <Link to={guidePath(s)} className="group block">
                        <span className="text-white font-medium group-hover:text-[#5B8CFF] transition-colors">
                          {GUIDES[s].title}
                        </span>
                        <span className="block text-sm text-neutral-500 mt-1 leading-relaxed">
                          {GUIDES[s].summary}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-6 font-mono-t text-[10px] uppercase tracking-widest text-neutral-600">
                  In progress
                </p>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── Article ──────────────────────────────────────────────────────────────────

export default function GuidesPage({ slug }) {
  const guide = slug ? GUIDES[slug] : null;

  if (slug && !guide) return <Navigate to="/guides" replace />;
  if (!slug) return <GuidesHome />;

  const cluster = clustersWithGuides().find((c) => c.id === guide.cluster);
  const siblings = cluster ? cluster.slugs.filter((s) => s !== slug) : [];

  return (
    <div data-testid={`guide-${slug}`} className="pt-24 md:pt-28 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid xl:grid-cols-[minmax(0,1fr)_200px] gap-x-12">
        <article className="min-w-0 max-w-[46rem] xl:justify-self-end">
          <nav aria-label="Breadcrumb" className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-400 flex flex-wrap items-center gap-2">
            <Link to="/guides" className="hover:text-white transition-colors">Guides</Link>
            {cluster && (<><span aria-hidden>/</span><span className="text-neutral-500">{cluster.title}</span></>)}
          </nav>

          <h1 className="mt-4 font-heading font-medium tracking-tight leading-[1.05] text-3xl md:text-[2.6rem] text-white">
            {guide.title}
          </h1>

          {/* The direct answer, before anything else. */}
          {guide.answer && (
            <p className="mt-6 text-lg text-neutral-200 leading-relaxed border-l-2 border-[#0047FF] pl-5">
              {guide.answer}
            </p>
          )}

          <p className="mt-6 font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">
            {AUTHOR.name}
            <span aria-hidden className="mx-2">·</span>
            <time dateTime={guide.updated || guide.published}>
              Updated {guide.updated || guide.published}
            </time>
          </p>

          <hr className="mt-8 border-white/10" />

          {guide.sections.map((s, i) => {
            const id = slugify(s.h);
            return (
              <section key={s.h} id={id} className={`scroll-mt-28 ${i ? "mt-12" : "mt-10"}`}>
                <h2 className="group font-heading font-medium tracking-tight text-[1.6rem] text-white">
                  {s.h}
                  <a
                    href={`#${id}`}
                    aria-label={`Link to ${s.h}`}
                    className="ml-2 align-middle text-[#5B8CFF] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-base"
                  >
                    #
                  </a>
                </h2>
                <div className="mt-4 space-y-5 text-[15.5px] text-neutral-400 leading-[1.75]">
                  {s.blocks.map((b, j) => <Block key={j} b={b} />)}
                </div>
              </section>
            );
          })}

          {guide.related && guide.related.length > 0 && (
            <section className="mt-16 border-t border-white/10 pt-8">
              <h2 className="font-heading font-medium text-xl text-white">See it on OwnPBX</h2>
              <ul className="mt-5 grid sm:grid-cols-2 gap-4">
                {guide.related.map((r) => (
                  <li key={r.to}>
                    <Link
                      to={r.to}
                      className="group block border border-white/10 bg-white/[0.02] p-4 hover:border-[#5B8CFF] transition-colors h-full"
                    >
                      <span className="text-white font-medium group-hover:text-[#5B8CFF] transition-colors">
                        {r.label}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {siblings.length > 0 && (
            <nav aria-label="More guides" className="mt-12">
              <p className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-400 mb-4">
                More in {cluster.title}
              </p>
              <ul className="space-y-3">
                {siblings.map((s) => (
                  <li key={s}>
                    <Link to={guidePath(s)} className="text-white hover:text-[#5B8CFF] transition-colors">
                      {GUIDES[s].title}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <p className="mt-12 text-sm text-neutral-500">
            Something wrong or missing here? Email{" "}
            <a href="mailto:ops@ownpbx.in" className="text-white underline underline-offset-4 hover:text-[#5B8CFF]">ops@ownpbx.in</a>.
          </p>
        </article>

        <OnThisPage sections={guide.sections} />
      </div>
    </div>
  );
}
